/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Draggable from 'react-draggable';
import {
  Apartment,
  Close,
  Construction,
  GpsFixed,
  GpsNotFixed,
  Lock,
  LockOpen,
  Park,
} from '@mui/icons-material';

import { useIsSmallMobile } from '../../../hooks/useIsSmallMobile';
import { useIsTabletOrMobile } from '../../../hooks/useIsTabletOrMobile';
import { useRolePermissions } from '../../../hooks/useRolePermissions';
import AssetIcon from '../../shared/atoms/AssetIcon';
import formatLatLng from '../../../utils/formatLatLng';
import OverallStatusLabel from '../../OverallStatusLabel';
import AssetReadingsList from './AssetReadingsList';
import { useLocationsCacheContext } from '../../../context/LocationsCacheContext';
import CustomTooltip from '../../shared/atoms/CustomTooltip';
import LayersList from './LayersList';
import { findItemWithId } from './util';
import { useMapPopUpsContext } from '../../../context/MapPopUpsContext';
import { closeAnimated } from '../../../redux/actions/sideDrawerActions';
import CopyCoordinatesIconButton from '../../shared/molecules/CopyCoordinatesIconButton';
import AddressIconButton from '../../shared/molecules/AddressIconButton';
import ActuatorControlPanel from '../../ActuatorControlPanel';
import { primaryColorObj } from '../../../themes';
import ActuatorIconWithStatus from '../../ActuatorIconWithStatus';

const PopupWrapper = ({
  onAction,
  item,
  goToDetails,
  closePopup,
  closeAllPopups,
  closeAllPopupsButCurrent,
  handleLockPress,
  initialIsLockValue,
  position,
  setPosition,
  hideDescription,
}) => {
  const { locations, buildings, assets } = useLocationsCacheContext();
  const { updating, follow, setFollow } = useMapPopUpsContext();
  const cardRef = useRef(null);
  const [isLock, setIsLock] = useState(initialIsLockValue);
  const [isDragging, setIsDragging] = useState(false);

  const isTabletOrMobile = useIsTabletOrMobile();
  const isMobile = useIsSmallMobile();
  const { data: userPermissions } = useRolePermissions();

  const { t } = useTranslation();

  const dispatch = useDispatch();
  const closeAnimatedDispatch = useCallback(
    args => dispatch(closeAnimated(args)),
    [dispatch],
  );

  const itemFound = useMemo(
    () => findItemWithId(item, assets, buildings, locations),
    [item, assets, buildings, locations],
  );

  const canFollow = itemFound?.properties?.sensors?.some(
    s => s.measuring_mode === 'positioning',
  );

  const closePopupHandler = (_event, reason) => {
    if (reason && reason === 'backdropClick') return;
    closePopup(item.id);
  };

  useEffect(() => {
    if (isTabletOrMobile) closeAllPopupsButCurrent?.(item.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const positionDisplay = useMemo(() => {
    if (!item.type && itemFound?.properties?.is_indoor)
      return itemFound?.properties?.zone_name;
    if (!item.type || item.type === 'BLD')
      return formatLatLng(
        itemFound?.geometry?.coordinates[1],
        itemFound?.geometry?.coordinates[0],
      );
    if (item.type === 'LOC')
      return formatLatLng(
        itemFound?.properties?.lat,
        itemFound?.properties?.lng,
      );
    if (item.type === 'FLR') return itemFound?.properties?.building_name;
    if (item.type === 'ZON') return itemFound?.properties?.floor_name;
  }, [
    item.type,
    itemFound?.geometry?.coordinates,
    itemFound?.properties?.building_name,
    itemFound?.properties?.floor_name,
    itemFound?.properties?.is_indoor,
    itemFound?.properties?.lat,
    itemFound?.properties?.lng,
    itemFound?.properties?.zone_name,
  ]);

  const goToTickets = () => {
    if (!userPermissions?.management?.['service-tickets']?.see) return;
    goToDetails(item.id);
  };

  const listLength = useMemo(() => {
    if (!item.type)
      return (
        (itemFound?.properties?.sensors?.length ?? 0) +
        (itemFound?.properties?.actuators?.length ?? 0)
      );
    if (item.type === 'ACTUATOR')
      return itemFound?.properties?.exposes?.length ?? 0;
    if (item.type === 'LOC') {
      const locationBuildings = buildings
        .filter(b => b.properties.parent === item.id)
        .map(b => b.properties);
      const locationAssets = assets
        .filter(
          a => a.properties.location === item.id && a.properties.is_placed,
        )
        .map(a => a.properties);
      return locationBuildings.length + locationAssets.length;
    }
    if (item.type === 'BLD') return itemFound?.properties?.floors?.length ?? 0;
    if (item.type === 'FLR') return itemFound?.properties?.zones?.length ?? 0;
    if (item.type === 'ZON') return itemFound?.properties?.assets?.length ?? 0;
  }, [item.type, item.id, itemFound?.properties, buildings, assets]);

  const splitCols = listLength > 10;

  const onActionCloseMenu = useCallback(
    (...props) => {
      onAction(...props);
      closeAnimatedDispatch();
    },
    [closeAnimatedDispatch, onAction],
  );

  const displayList = useMemo(() => {
    if (!item.type)
      return (
        <AssetReadingsList
          sensors={itemFound?.properties?.sensors}
          actuators={itemFound?.properties?.actuators}
          onAction={onActionCloseMenu}
        />
      );
    if (item.type === 'ACTUATOR') {
      const actuator = itemFound?.properties;
      if (!actuator) return;
      return (
        <Box
          sx={{
            height: isTabletOrMobile ? '200px' : '500px',
            '& .MuiCard-root': {
              background: 'transparent',
            },
            '& .actuator-mini-enum-control': {
              background: primaryColorObj.default,
            },
            '& .MuiCardContent-root:last-child': {
              paddingBottom: '1rem',
            },
          }}
        >
          <ActuatorControlPanel
            gateway={actuator.gateway}
            deviceId={actuator.id}
            deviceName={actuator.name}
            exposes={actuator.exposes}
            icon={actuator.icon}
            type="mini"
            noHeader
          />
        </Box>
      );
    }
    if (item.type === 'LOC') {
      const locationBuildings = buildings
        .filter(b => b.properties.parent === item.id)
        .map(b => b.properties);
      const locationAssets = assets
        .filter(
          a => a.properties.location === item.id && a.properties.is_placed,
        )
        .map(a => a.properties);
      return (
        <Stack>
          <Stack direction="row" spacing={1} alignItems="center" px={2} pt={2}>
            <Apartment fontSize="small" />
            <Typography variant="body2" fontWeight="bold">
              {t(`components.location.buildings`, {
                count: locationBuildings.length,
              })}
            </Typography>
          </Stack>
          <LayersList
            list={locationBuildings}
            onAction={onActionCloseMenu}
            hideDescription={hideDescription}
            splitCols={splitCols}
          />
          <Box px={2}>
            <Divider
              sx={{
                borderColor: theme => theme.palette.primary.main,
                width: '100%',
                borderBottomWidth: 1,
                opacity: 0.5,
              }}
            />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" px={2} pt={2}>
            <Park fontSize="small" />
            <Typography variant="body2" fontWeight="bold">
              {t(`components.location.assets`, {
                count: locationAssets.length,
              })}
            </Typography>
          </Stack>
          <LayersList
            list={locationAssets}
            onAction={onActionCloseMenu}
            hideDescription={hideDescription}
            splitCols={splitCols}
          />
        </Stack>
      );
    }
    if (item.type === 'BLD')
      return (
        <LayersList
          list={itemFound?.properties?.floors}
          onAction={onActionCloseMenu}
          hideDescription={hideDescription}
          splitCols={splitCols}
        />
      );
    if (item.type === 'FLR')
      return (
        <LayersList
          list={itemFound?.properties?.zones}
          onAction={onActionCloseMenu}
          hideDescription={hideDescription}
          splitCols={splitCols}
        />
      );
    if (item.type === 'ZON')
      return (
        <LayersList
          list={itemFound?.properties?.assets}
          onAction={onActionCloseMenu}
          hideDescription={hideDescription}
          splitCols={splitCols}
        />
      );
  }, [
    item.type,
    item.id,
    itemFound?.properties,
    onActionCloseMenu,
    hideDescription,
    isTabletOrMobile,
    buildings,
    assets,
    t,
    splitCols,
  ]);

  const isFollowing = useMemo(
    () => follow?.id === itemFound?.properties?.id,
    [follow?.id, itemFound?.properties?.id],
  );

  const goToFacilityDetails = useCallback(() => {
    if (item.type === 'ACTUATOR')
      return onAction(
        itemFound?.properties?.asset,
        false,
        `/actuators/${itemFound?.properties?.id}`,
      );
    return onAction(itemFound?.properties?.id, false);
  }, [item, onAction, itemFound]);

  const getLockTitle = () => {
    if (!isLock) return t('components.pop_up.tooltips_text.lock');
    if (updating) return t('components.pop_up.tooltips_text.locking');
    return t('components.pop_up.tooltips_text.unlock');
  };

  const handleLock = async () => {
    if (isLock) {
      setIsLock(false);
      return;
    }

    setIsLock(true);
    try {
      await handleLockPress({
        left: cardRef.current?.getBoundingClientRect().left,
        top: cardRef.current?.getBoundingClientRect().top,
        width: cardRef.current?.getBoundingClientRect().width,
        window: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      });
    } catch {
      setIsLock(false);
    }
  };

  return (
    itemFound?.properties && (
      <Draggable
        disabled={isMobile || isLock || !closePopup}
        handle=".MuiCard-root"
        cancel={'[class*="MuiButtonBase-root"],.close'}
        key={`draggable-${item.id}${
          position ? `-top-${position.top}-left-${position.left}` : ''
        }`}
        onStart={() => {
          setIsDragging(true);
        }}
        onStop={() => {
          setIsDragging(false);
          setPosition?.(prev => ({
            ...prev,
            left: cardRef.current?.getBoundingClientRect().left,
            top: cardRef.current?.getBoundingClientRect().top,
          }));
        }}
      >
        <Card
          ref={cardRef}
          sx={{
            cursor: !isLock && closePopup ? 'move' : 'auto',
            background:
              'linear-gradient(rgb(8, 48, 64) 0%, rgb(16, 106, 120) 100%)',
            color: 'white',
            margin: '0 !important',
            maxHeight: 'calc(100vh - 50px)',
            visibility: 'visible',
            userSelect: isDragging ? 'none' : 'auto',
            width: splitCols ? '600px' : '300px',
            height: 'auto',
          }}
        >
          <CardContent
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: 0,
            }}
          >
            <Stack direction="row" alignItems="center" paddingTop="10px">
              {itemFound?.properties?.status && (
                <Box paddingRight="10px">
                  <AssetIcon status={itemFound?.properties?.status} />
                </Box>
              )}
              {item.type === 'ACTUATOR' && (
                <Box paddingRight="5px">
                  <ActuatorIconWithStatus
                    type="library"
                    name={itemFound?.properties?.icon}
                    actuator={itemFound?.properties}
                  />
                </Box>
              )}
              <Typography
                variant="h5"
                color="primary"
                paddingBottom={item.type === 'ACTUATOR' ? '10px' : 0}
                textAlign="left"
              >
                {itemFound?.properties?.name}
              </Typography>
            </Stack>
            {positionDisplay && (
              <Typography variant="subtitle2" textAlign="left">
                {positionDisplay}
              </Typography>
            )}
            {itemFound?.properties?.status &&
              itemFound?.properties?.message && (
                <OverallStatusLabel
                  status={itemFound?.properties?.status}
                  message={itemFound?.properties?.message}
                />
              )}
            <Stack
              position="absolute"
              top="5px"
              right="5px"
              direction="row"
              spacing="5px"
            >
              {(!item.type || item.type === 'BLD' || item.type === 'LOC') &&
                !itemFound?.properties?.is_indoor && (
                  <>
                    {itemFound?.properties?.data?.address && (
                      <AddressIconButton
                        address={itemFound?.properties?.data?.address}
                      />
                    )}
                    <CopyCoordinatesIconButton
                      lat={item.lat || itemFound?.geometry?.coordinates[1]}
                      lng={item.lng || itemFound?.geometry?.coordinates[0]}
                      className="close"
                    />
                  </>
                )}

              {canFollow && (
                <CustomTooltip
                  title={
                    isFollowing
                      ? t('components.pop_up.tooltips_text.unfollow')
                      : t('components.pop_up.tooltips_text.follow')
                  }
                >
                  <span>
                    <IconButton
                      disabled={updating}
                      onClick={async () => {
                        if (isFollowing) setFollow(null);
                        else {
                          if (!isLock) await handleLock();
                          setFollow({
                            id: itemFound?.properties?.id,
                            name: itemFound?.properties?.name,
                          });
                          window.map.flyTo({
                            center: itemFound?.geometry?.coordinates,
                          });
                        }
                      }}
                      size="small"
                      className="close"
                    >
                      {isFollowing ? (
                        <GpsFixed sx={{ color: '#00ffff' }} />
                      ) : (
                        <GpsNotFixed sx={{ color: 'white' }} />
                      )}
                    </IconButton>
                  </span>
                </CustomTooltip>
              )}
              {handleLockPress && !isTabletOrMobile && (
                <CustomTooltip title={getLockTitle()}>
                  <span>
                    <IconButton
                      onClick={handleLock}
                      size="small"
                      className="close"
                    >
                      {isLock ? (
                        <Lock sx={{ color: '#00ffff' }} />
                      ) : (
                        <LockOpen sx={{ color: 'white' }} />
                      )}
                    </IconButton>
                  </span>
                </CustomTooltip>
              )}
              {closePopup && (
                <CustomTooltip
                  title={t('components.pop_up.tooltips_text.close')}
                >
                  <span>
                    <IconButton
                      onClick={closePopupHandler}
                      size="small"
                      className="close"
                    >
                      <Close sx={{ color: 'white' }} />
                    </IconButton>
                  </span>
                </CustomTooltip>
              )}
            </Stack>
          </CardContent>
          <CardContent
            sx={{
              padding: 0,
            }}
          >
            {displayList}
          </CardContent>
          <CardActions
            sx={theme => ({
              background: theme.palette.primary.main,
              display: 'flex',
              justifyContent:
                itemFound?.properties?.ticketCount > 0
                  ? 'space-between'
                  : 'flex-end',
              padding: 0,
            })}
          >
            {itemFound?.properties?.ticketCount > 0 && (
              <IconButton aria-label="cart" onClick={goToTickets}>
                <Badge
                  badgeContent={itemFound?.properties?.ticketCount}
                  color="secondary"
                  sx={theme => ({
                    '& .MuiBadge-badge': {
                      right: -10,
                      top: 13,
                      border: `2px solid ${theme.palette.background.paper}`,
                      padding: '0 4px',
                    },
                  })}
                >
                  <Construction sx={{ color: 'white' }} />
                </Badge>
              </IconButton>
            )}
            <Button
              variant="text"
              onMouseDown={e => e.stopPropagation()}
              onClick={() => {
                goToFacilityDetails();
                if (closeAllPopups) closeAllPopups();
                if (item.type !== 'LOC') closeAnimatedDispatch();
              }}
              tabIndex={0}
              color="secondary"
              sx={{ fontWeight: 'bold' }}
            >
              {item.type === 'LOC'
                ? t('components.location.go_to')
                : t('components.pop_up.view_details')}
            </Button>
          </CardActions>
        </Card>
      </Draggable>
    )
  );
};

PopupWrapper.defaultProps = {
  initialIsLockValue: false,
  hideDescription: false,
};

PopupWrapper.propTypes = {
  onAction: PropTypes.func.isRequired,
  goToDetails: PropTypes.func,
  closePopup: PropTypes.func,
  handleLockPress: PropTypes.func,
  initialIsLockValue: PropTypes.bool,
  closeAllPopups: PropTypes.func,
  closeAllPopupsButCurrent: PropTypes.func,
  item: PropTypes.object.isRequired,
  position: PropTypes.object,
  setPosition: PropTypes.func,
  hideDescription: PropTypes.bool,
};

export default PopupWrapper;
